'use client';
import React from 'react';
import Link from 'next/link';

type Locale = 'fr' | 'en' | 'es';

interface MarketingFooterProps {
  locale: Locale;
  onLocaleChange?: (locale: Locale) => void;
}

// ─── Supabase-bolt style pictogram — thick & filled ──────────────────────────
function LoyzenPicto() {
  return (
    <svg width="26" height="26" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <circle cx="18" cy="18" r="15" stroke="#3ecf8e" strokeWidth="1.8" strokeDasharray="4 2.5" opacity="0.4" />
      <line x1="18" y1="12.5" x2="18" y2="5" stroke="#3ecf8e" strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />
      <line x1="21" y1="20" x2="28" y2="29" stroke="#3ecf8e" strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />
      <line x1="15" y1="20" x2="8" y2="29" stroke="#3ecf8e" strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />
      <circle cx="18" cy="3.5" r="3.5" fill="#3ecf8e" />
      <circle cx="30" cy="30.5" r="3.5" fill="#3ecf8e" />
      <circle cx="6" cy="30.5" r="3.5" fill="#3ecf8e" />
      <circle cx="18" cy="18" r="6" fill="#3ecf8e" />
      <circle cx="18" cy="18" r="2.8" fill="#d1fae5" opacity="0.95" />
    </svg>
  );
}

// ─── Flag SVGs ────────────────────────────────────────────────────────────────
function FlagFR() {
  return (
    <svg width="18" height="13" viewBox="0 0 18 13" fill="none" aria-hidden="true">
      <rect width="18" height="13" rx="2" fill="#ED2939" />
      <rect width="12" height="13" fill="#fff" />
      <rect width="6" height="13" fill="#002395" />
    </svg>
  );
}

// US flag for English
function FlagUS() {
  return (
    <svg width="18" height="13" viewBox="0 0 18 13" fill="none" aria-hidden="true">
      <rect width="18" height="13" rx="2" fill="#B22234" />
      <rect y="1" width="18" height="1" fill="white" />
      <rect y="3" width="18" height="1" fill="white" />
      <rect y="5" width="18" height="1" fill="white" />
      <rect y="7" width="18" height="1" fill="white" />
      <rect y="9" width="18" height="1" fill="white" />
      <rect y="11" width="18" height="1" fill="white" />
      <rect width="7" height="7" fill="#3C3B6E" />
    </svg>
  );
}

function FlagES() {
  return (
    <svg width="18" height="13" viewBox="0 0 18 13" fill="none" aria-hidden="true">
      <rect width="18" height="13" rx="2" fill="#AA151B" />
      <rect y="3.25" width="18" height="6.5" fill="#F1BF00" />
    </svg>
  );
}

const footerContent: Record<Locale, {
  tagline: string;
  cols: { title: string; links: { label: string; href: string }[] }[];
  copyright: string;
  language: string;
  status: string;
}> = {
  fr: {
    tagline: 'La fidélité sans carte, sans application.',
    cols: [
      {
        title: 'Produit',
        links: [
          { label: 'Fonctionnement', href: '/how-it-works' },
          { label: 'Fonctionnalités', href: '/features' },
          { label: 'Récompenses', href: '/features' },
          { label: 'Équipe & Sécurité', href: '/features' },
          { label: 'Pilotage', href: '/features' },
        ],
      },
      {
        title: 'Ressources',
        links: [
          { label: 'Tarifs', href: '/pricing' },
          { label: 'FAQ', href: '/#faq' },
          { label: 'Se connecter', href: '/login' },
          { label: 'Créer un programme', href: '/signup' },
        ],
      },
      {
        title: 'Légal',
        links: [
          { label: 'Confidentialité', href: '/privacy' },
          { label: "Conditions d'utilisation", href: '/terms' },
        ],
      },
    ],
    copyright: 'Tous droits réservés.',
    language: 'Langue',
    status: 'Opérationnel',
  },
  en: {
    tagline: 'Cardless loyalty, no app required.',
    cols: [
      {
        title: 'Product',
        links: [
          { label: 'How it works', href: '/how-it-works' },
          { label: 'Features', href: '/features' },
          { label: 'Rewards', href: '/features' },
          { label: 'Team & Security', href: '/features' },
          { label: 'Analytics', href: '/features' },
        ],
      },
      {
        title: 'Resources',
        links: [
          { label: 'Pricing', href: '/pricing' },
          { label: 'FAQ', href: '/#faq' },
          { label: 'Sign in', href: '/login' },
          { label: 'Create a program', href: '/signup' },
        ],
      },
      {
        title: 'Legal',
        links: [
          { label: 'Privacy', href: '/privacy' },
          { label: 'Terms of use', href: '/terms' },
        ],
      },
    ],
    copyright: 'All rights reserved.',
    language: 'Language',
    status: 'Operational',
  },
  es: {
    tagline: 'Fidelización sin tarjeta, sin aplicación.',
    cols: [
      {
        title: 'Producto',
        links: [
          { label: 'Cómo funciona', href: '/how-it-works' },
          { label: 'Funcionalidades', href: '/features' },
          { label: 'Recompensas', href: '/features' },
          { label: 'Equipo & Seguridad', href: '/features' },
          { label: 'Control', href: '/features' },
        ],
      },
      {
        title: 'Recursos',
        links: [
          { label: 'Precios', href: '/pricing' },
          { label: 'FAQ', href: '/#faq' },
          { label: 'Iniciar sesión', href: '/login' },
          { label: 'Crear un programa', href: '/signup' },
        ],
      },
      {
        title: 'Legal',
        links: [
          { label: 'Privacidad', href: '/privacy' },
          { label: 'Términos de uso', href: '/terms' },
        ],
      },
    ],
    copyright: 'Todos los derechos reservados.',
    language: 'Idioma',
    status: 'Operacional',
  },
};

// US flag for English — "Español" only (no country suffix)
const languageOptions: { value: Locale; label: string; Flag: React.FC }[] = [
  { value: 'fr', label: 'Français', Flag: FlagFR },
  { value: 'en', label: 'English', Flag: FlagUS },
  { value: 'es', label: 'Español', Flag: FlagES },
];

export default function MarketingFooter({ locale, onLocaleChange }: MarketingFooterProps) {
  const t = footerContent[locale];

  return (
    <footer className="relative text-[#64748b] overflow-hidden" style={{ background: '#080808', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      {/* Subtle top glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(62,207,142,0.15), transparent)' }} />

      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-16 py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10 lg:gap-8">

          {/* Brand — 2 cols */}
          <div className="col-span-2">
            <Link href="/" className="flex items-center gap-2.5 group mb-5 w-fit">
              <LoyzenPicto />
              <span
                className="text-white select-none"
                style={{
                  fontFamily: '"DM Sans", "Plus Jakarta Sans", system-ui, sans-serif',
                  fontWeight: 800,
                  fontSize: '18px',
                  letterSpacing: '-0.035em',
                }}
              >
                loyzen
              </span>
            </Link>
            <p className="text-[13px] leading-relaxed max-w-[200px] mb-6" style={{ color: '#52525b' }}>{t.tagline}</p>

            {/* Language selector */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.1em] mb-3" style={{ color: '#3f3f46' }}>{t.language}</p>
              <div className="flex flex-col gap-1">
                {languageOptions.map(({ value, label, Flag }) => {
                  const isActive = locale === value;
                  return (
                    <button
                      key={value}
                      onClick={() => onLocaleChange?.(value)}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12px] font-normal transition-all duration-150 text-left w-fit"
                      style={isActive ? {
                        background: 'rgba(62,207,142,0.08)',
                        color: '#3ecf8e',
                        border: '1px solid rgba(62,207,142,0.2)'
                      } : {
                        color: '#71717a',
                        border: '1px solid transparent'
                      }}
                    >
                      {React.createElement(Flag)}
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Link columns */}
          {t.cols.map((col) => (
            <div key={col.title}>
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.1em] mb-4" style={{ color: '#52525b' }}>{col.title}</h3>
              <ul className="space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.href + link.label}>
                    <Link
                      href={link.href}
                      className="text-[13px] font-light transition-colors duration-150 hover:text-white"
                      style={{ color: '#71717a' }}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="mt-14 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <p className="text-[12px]" style={{ color: '#3f3f46' }}>
            © {new Date().getFullYear()} Loyzen. {t.copyright}
          </p>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#3ecf8e]" />
            <span className="text-[11px]" style={{ color: '#3ecf8e' }}>{t.status}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
